import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Button, Collapse, Typography, Space, Row, Col, message } from 'antd';
import { biometricCategories } from '../utils/biometricParameters';
import { getFormValidationRules, getValidationMetadata } from '../utils/biometricValidation';
import { transformToStandardizedOptimized } from '../utils/biometricParameterCache';

const { Panel } = Collapse;
const { Title } = Typography;

const ComprehensiveBiometricsForm = ({ patientId, initialData, onSave, onCancel }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (initialData) {
      // Apply client-side corruption fix for vision data before setting form values
      const correctedData = { ...initialData };

      // Fix any corrupted vision data that might have been retrieved from the database
      Object.keys(correctedData).forEach(key => {
        if (key.includes('biometrics_vision_visualAcuity') && typeof correctedData[key] === 'number') {
          const value = correctedData[key];
          const valueStr = value.toString();
          // Convert numbers like 2020, 2025, 2040 back to Snellen format
          if (valueStr.length === 4 && valueStr.startsWith('20')) {
            const denominator = valueStr.substring(2);
            const denominatorNum = parseInt(denominator, 10);
            if (denominatorNum > 0 && denominatorNum < 1000) {
              correctedData[key] = `20/${denominator}`;
              console.log(`[CLIENT FIX] Corrected vision data: ${key} from ${value} to ${correctedData[key]}`);
            }
          }
          // Also handle simple numeric values like 20, 25, 40 for pinhole parameters
          // Extended range to handle edge cases like 6, 7 that might represent 20/60, 20/70
          else if (valueStr.length <= 3 && !isNaN(value) && value >= 1 && value <= 400) {
            // Special handling for single digits that might represent tens (6 -> 60, 7 -> 70)
            let denominator = value;
            if (value < 10 && value >= 1) {
              denominator = value * 10; // 6 -> 60, 7 -> 70
              console.log(`[CLIENT FIX] Interpreting single digit ${value} as ${denominator} for Snellen format`);
            }
            correctedData[key] = `20/${denominator}`;
            console.log(`[CLIENT FIX] Converted simple numeric vision data: ${key} from ${value} to ${correctedData[key]}`);
          }
        }
      });

      form.setFieldsValue(correctedData);
    } else {
      form.resetFields();
    }
  }, [initialData, form]);

  const handleFinish = (values) => {
    // Filter out any undefined values which might occur if a panel is not opened
    const cleanedValues = {};
    for (const key in values) {
      if (values[key] !== undefined) {
        let value = values[key];

        // Ensure vision values are strings, not numbers
        if (key.includes('biometrics_vision_visualAcuity') && typeof value === 'number') {
          const valueStr = value.toString();
          if (valueStr.length === 4 && valueStr.startsWith('20')) {
            const denominator = valueStr.substring(2);
            const denominatorNum = parseInt(denominator, 10);
            if (denominatorNum > 0 && denominatorNum < 1000) {
              value = `20/${denominator}`;
              console.log(`[SUBMIT FIX] Corrected vision data: ${key} from ${values[key]} to ${value}`);
            }
          }
          // Also handle simple numeric values like 20, 25, 40 for pinhole parameters
          // Extended range to handle edge cases like 6, 7 that might represent 20/60, 20/70
          else if (valueStr.length <= 3 && !isNaN(value) && value >= 1 && value <= 400) {
            // Special handling for single digits that might represent tens (6 -> 60, 7 -> 70)
            let denominator = value;
            if (value < 10 && value >= 1) {
              denominator = value * 10; // 6 -> 60, 7 -> 70
              console.log(`[SUBMIT FIX] Interpreting single digit ${value} as ${denominator} for Snellen format`);
            }
            value = `20/${denominator}`;
            console.log(`[SUBMIT FIX] Converted simple numeric vision data: ${key} from ${values[key]} to ${value}`);
          }
        }

        cleanedValues[key] = value;
      }
    }

    // Transform raw parameter names to standardized format using optimized function
    const standardizedValues = transformToStandardizedOptimized(cleanedValues);

    console.log('Biometrics form submission (optimized):', {
      originalKeys: Object.keys(cleanedValues),
      standardizedKeys: Object.keys(standardizedValues),
      totalParameters: Object.keys(standardizedValues).length
    });

    onSave(standardizedValues); // Pass standardized biometric data
  };

  const renderParameterInput = (parameter) => {
    // Get validation metadata for enhanced placeholder and input behavior
    const validationMetadata = getValidationMetadata(parameter.key);

    // Disable calculated Y-Balance Test fields
    if (parameter.key.startsWith('ybt_') && (parameter.key.endsWith('_avg') || parameter.key.endsWith('_deviation') || parameter.key.includes('_composite_reach'))) {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Auto-calculated"
          addonAfter={parameter.units}
          disabled
        />
      );
    }

    if (parameter.key === 'waistHipRatio') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Auto-calculated"
          addonAfter={parameter.units}
          disabled
        />
      );
    }

    if (parameter.key === 'ree' || parameter.key === 'tee') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Auto-calculated"
          addonAfter={parameter.units}
          disabled
        />
      );
    }

    if (parameter.key === 'labs_lipid_non_hdl' || parameter.key === 'labs_lipid_tc_glu_ratio') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Auto-calculated"
          addonAfter={parameter.units}
          disabled
        />
      );
    }

    if (parameter.key === 'ree' || parameter.key === 'tee') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Auto-calculated"
          addonAfter={parameter.units}
          disabled
        />
      );
    }

    // Special handling for vision parameters (Snellen and Jaeger values are strings, not numbers)
    const isVisionParameter = parameter.units === 'Snellen' || parameter.units === 'Jaeger';

    // Debug log for vision parameters
    if (parameter.key && parameter.key.includes('vision')) {
      console.log(`[DEBUG] Vision parameter: ${parameter.key}, units: ${parameter.units}, isVisionParameter: ${isVisionParameter}`);
    }

    // Determine placeholder text from validation metadata or parameter info
    let placeholder = parameter.name;
    if (validationMetadata && validationMetadata.placeholder) {
      placeholder = validationMetadata.placeholder;
    } else if (parameter.units === 'Snellen') {
      placeholder = 'e.g., 20/20, CF, HM';
    } else if (parameter.units === 'Jaeger') {
      placeholder = 'e.g., J1, J2+';
    } else if (parameter.units) {
      placeholder = parameter.units;
    }

    // Basic input type determination, can be expanded
    if ((parameter.units || typeof parameter.defaultValue === 'number') && !isVisionParameter) {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder={placeholder}
          min={validationMetadata?.min}
          max={validationMetadata?.max}
        />
      );
    }

    // For vision parameters, add onChange handler to ensure string format
    if (isVisionParameter) {
      return (
        <Input
          placeholder={placeholder}
          onChange={(e) => {
            let value = e.target.value;
            // If someone somehow enters a number, try to convert it to proper format
            if (parameter.units === 'Snellen') {
              // Handle 4-digit numbers like 2020, 2025, 2040
              if (/^\d{4}$/.test(value) && value.startsWith('20')) {
                const denominator = value.substring(2);
                const denominatorNum = parseInt(denominator, 10);
                if (denominatorNum > 0 && denominatorNum < 1000) {
                  value = `20/${denominator}`;
                  // Update the form field with the corrected value
                  setTimeout(() => {
                    form.setFieldValue(parameter.key, value);
                  }, 0);
                }
              }
              // Handle simple numeric values like 20, 25, 40 for pinhole parameters
              else if (/^\d{1,3}$/.test(value)) {
                const numValue = parseInt(value, 10);
                if (numValue >= 10 && numValue <= 400) {
                  value = `20/${value}`;
                  // Update the form field with the corrected value
                  setTimeout(() => {
                    form.setFieldValue(parameter.key, value);
                  }, 0);
                }
              }
            }
          }}
        />
      );
    }

    return <Input placeholder={placeholder} />;
  };

  const getValidationRules = (parameter) => {
    const rules = [];

    // Add validation rules for vision parameters
    if (parameter.units === 'Snellen') {
      rules.push({
        pattern: /^20\/\d+$/,
        message: 'Invalid Snellen format (e.g., 20/20)'
      });
    } else if (parameter.units === 'Jaeger') {
      rules.push({
        pattern: /^J\d+([+-])?$/,
        message: 'Invalid Jaeger format (e.g., J1, J2+, J16-)'
      });
    }

    return rules;
  };

  // Helper function to organize parameters into two columns
  const organizeParametersIntoColumns = (parameters) => {
    const leftColumn = [];
    const rightColumn = [];

    // Check if this category has left/right variants
    const hasLeftRightVariants = parameters.some(param =>
      param.key.includes('_left') || param.key.includes('_right')
    );

    if (hasLeftRightVariants) {
      // Group by left/right for categories with left/right variants
      parameters.forEach(param => {
        if (param.key.includes('_left')) {
          leftColumn.push(param);
        } else if (param.key.includes('_right')) {
          rightColumn.push(param);
        } else {
          // For parameters without left/right designation (like "both eyes"), add to left column
          leftColumn.push(param);
        }
      });
    } else {
      // For categories without left/right variants, distribute evenly
      parameters.forEach((param, index) => {
        if (index % 2 === 0) {
          leftColumn.push(param);
        } else {
          rightColumn.push(param);
        }
      });
    }

    return { leftColumn, rightColumn };
  };

  const renderCognitionCategory = (parameters) => {
    const groupedParams = {};
    const otherParams = [];

    parameters.forEach(param => {
      if (param.key.startsWith('stroop_')) {
        const key = param.key.replace('_time', '').replace('_errors', '').replace('_error', '');
        if (!groupedParams[key]) {
          groupedParams[key] = {};
        }
        if (param.key.endsWith('_time')) {
          groupedParams[key].time = param;
        } else if (param.key.endsWith('_errors') || param.key.endsWith('_error')) {
          groupedParams[key].errors = param;
        }
      } else {
        otherParams.push(param);
      }
    });

    const { leftColumn, rightColumn } = organizeParametersIntoColumns(otherParams);

    return (
      <>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            {leftColumn.map(param => (
              <Form.Item
                key={param.key}
                name={param.key}
                label={`${param.name}${param.units ? ` (${param.units})` : ''}`}
                rules={getFormValidationRules(param.key)}
              >
                {renderParameterInput(param)}
              </Form.Item>
            ))}
          </Col>
          <Col xs={24} sm={12}>
            {rightColumn.map(param => (
              <Form.Item
                key={param.key}
                name={param.key}
                label={`${param.name}${param.units ? ` (${param.units})` : ''}`}
                rules={getFormValidationRules(param.key)}
              >
                {renderParameterInput(param)}
              </Form.Item>
            ))}
          </Col>
        </Row>
        {Object.keys(groupedParams).map(key => {
          const { time, errors } = groupedParams[key];
          return (
            <Row gutter={[16, 0]} key={key}>
              <Col xs={24} sm={12}>
                {time && (
                  <Form.Item
                    key={time.key}
                    name={time.key}
                    label={`${time.name}${time.units ? ` (${time.units})` : ''}`}
                    rules={getFormValidationRules(time.key)}
                  >
                    {renderParameterInput(time)}
                  </Form.Item>
                )}
              </Col>
              <Col xs={24} sm={12}>
                {errors && (
                  <Form.Item
                    key={errors.key}
                    name={errors.key}
                    label={`${errors.name}${errors.units ? ` (${errors.units})` : ''}`}
                    rules={getFormValidationRules(errors.key)}
                  >
                    {renderParameterInput(errors)}
                  </Form.Item>
                )}
              </Col>
            </Row>
          );
        })}
      </>
    );
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} name="comprehensive_biometrics_form">
      <Collapse defaultActiveKey={biometricCategories.map(cat => cat.key)} accordion={false}>
        {biometricCategories.map(category => {
          const availableParameters = category.parameters;

          // Skip empty categories
          if (availableParameters.length === 0) return null;

          if (category.key === 'cognition') {
            return (
              <Panel header={<Title level={5}>{category.title}</Title>} key={category.key}>
                {renderCognitionCategory(availableParameters)}
              </Panel>
            );
          }

          // Organize parameters into columns
          const { leftColumn, rightColumn } = organizeParametersIntoColumns(availableParameters);

          return (
            <Panel header={<Title level={5}>{category.title}</Title>} key={category.key}>
              <Row gutter={[16, 0]}>
                {/* Left Column */}
                <Col xs={24} sm={12}>
                  {leftColumn.map(param => (
                    <Form.Item
                      key={param.key}
                      name={param.key}
                      label={`${param.name}${param.units ? ` (${param.units})` : ''}`}
                      rules={getFormValidationRules(param.key)}
                    >
                      {renderParameterInput(param)}
                    </Form.Item>
                  ))}
                </Col>

                {/* Right Column */}
                <Col xs={24} sm={12}>
                  {rightColumn.map(param => (
                    <Form.Item
                      key={param.key}
                      name={param.key}
                      label={`${param.name}${param.units ? ` (${param.units})` : ''}`}
                      rules={getFormValidationRules(param.key)}
                    >
                      {renderParameterInput(param)}
                    </Form.Item>
                  ))}
                </Col>
              </Row>
            </Panel>
          );
        })}
      </Collapse>
      <Space style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button type="primary" htmlType="submit">
          Save Biometrics
        </Button>
      </Space>
    </Form>
  );
};

export default ComprehensiveBiometricsForm;
